import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import {
  ClearIcon,
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { calendarIcon, closeIcon, saveIcon } from "assets/images";
import React, { useEffect, useState } from "react";
import { IAllocateFormDetail } from "utils/interfaces/student-profile-details";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { DATE_ONLY_FORMAT, DATE_PICKER_FORMAT } from "utils/constants/constant";
import {
  AllocatePLAPayload,
  AllocatePLASuccessPayload,
} from "store/studentProfileDetails/types";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  ENTER_VALID_DATE_MSG,
  PAST_DATE_ERROR_MSG,
  PLA_ALLOCATED_MSG,
  PLA_NOT_ALLOCATED_MSG,
} from "utils/constants/Messages";
import {
  PLAUserListPayload,
  PLAUserListSuccessPayload,
} from "store/slpDashboard/type";
import { IPLAUserOption } from "utils/interfaces/slp";
import { getAllocatedToError } from "utils/helper/validation-message-helper";

function CalendarIcon() {
  return (
    <div className="calendar-icon">
      <img src={calendarIcon} alt="calendar" />
    </div>
  );
}

interface IAllocateSLPModalProps {
  slpId: number;
  allocatedToId: number | null;
  dueDate: string | null;
  openAllocateModal: boolean;
  setOpenAllocateModal: React.Dispatch<React.SetStateAction<boolean>>;
  allocatePLARequest: (payload: AllocatePLAPayload) => void;
  plaUserListRequest: (payload: PLAUserListPayload) => void;
  updateAllocatePLACallback?: () => void;
}

const AllocateSLPModal: React.FC<IAllocateSLPModalProps> = (props) => {
  const {
    slpId,
    allocatedToId,
    dueDate,
    plaUserListRequest,
    allocatePLARequest,
    setOpenAllocateModal,
    openAllocateModal,
    updateAllocatePLACallback,
  } = props;

  const [plaUserList, setPlaUserList] = useState<IPLAUserOption[]>();

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<IAllocateFormDetail>({
    defaultValues: {
      dueDate: dueDate !== null ? dueDate : dayjs().format(DATE_ONLY_FORMAT),
      allocatedTo: allocatedToId,
    },
  });

  const onPLAUserListSuccess = (response: PLAUserListSuccessPayload) => {
    setPlaUserList(response.userList);
    hideLoaderForBatchApiCall();
  };

  const getPLAUserList = () => {
    if (plaUserListRequest) {
      showLoaderForBatchApiCall();
      const payload = {
        callback: onPLAUserListSuccess,
      };

      plaUserListRequest(payload);
    }
  };

  useEffect(() => {
    getPLAUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseAllocateToPLA = () => {
    reset({ dueDate: null, allocatedTo: null });
    setOpenAllocateModal(false);
  };

  const onAllocatePLASuccess = (response: AllocatePLASuccessPayload) => {
    hideLoaderForBatchApiCall();
    handleCloseAllocateToPLA();
    if (response.isSuccessStatusCode) {
      toast.success(PLA_ALLOCATED_MSG);
      if (updateAllocatePLACallback) updateAllocatePLACallback();
    } else {
      toast.error(PLA_NOT_ALLOCATED_MSG);
    }
  };

  const onAllocatePLASubmit = async (data: IAllocateFormDetail) => {
    if (allocatePLARequest && slpId && data?.allocatedTo && data?.dueDate) {
      showLoaderForBatchApiCall();
      const payload = {
        values: {
          slpId,
          dueDate: data.dueDate,
          plaId: data.allocatedTo,
        },
        callback: onAllocatePLASuccess,
      };
      allocatePLARequest(payload);
    }
  };

  return (
    <Modal open={openAllocateModal} onClose={handleCloseAllocateToPLA}>
      <Box className="common-modal modal-400">
        <Box className="modal-header">
          <Typography variant="h4">Allocate PLA</Typography>
          <IconButton onClick={handleCloseAllocateToPLA}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onAllocatePLASubmit)}>
          <Box className="modal-body">
            <Grid container columnSpacing="16px">
              <Grid item xs={12}>
                <FormControl fullWidth className="select">
                  <InputLabel
                    id="allocated-to-select-label"
                    shrink={!!watch("allocatedTo")}
                  >
                    Allocated To <span className="color-red">*</span>
                  </InputLabel>
                  <Controller
                    name="allocatedTo"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="allocated-to-select-label"
                        id="course-select"
                        value={field.value ?? ""}
                        displayEmpty
                        error={!!errors.allocatedTo}
                        endAdornment={
                          !!field.value && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setValue("allocatedTo", null);
                              }}
                              edge="end"
                              className="clear-btn"
                            >
                              <ClearIcon />
                            </IconButton>
                          )
                        }
                      >
                        {plaUserList?.map((pla) => (
                          <MenuItem key={pla.id} value={pla.id}>
                            {pla.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {!!errors.allocatedTo && (
                    <FormHelperText error>
                      {getAllocatedToError(errors.allocatedTo)}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }} className="datepicker-box">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DesktopDatePicker"]}>
                      <Controller
                        control={control}
                        name="dueDate"
                        rules={{
                          required: true,
                          validate: (value) => {
                            const date = dayjs(value);
                            if (!date.isValid()) {
                              return ENTER_VALID_DATE_MSG;
                            }

                            if (date.isBefore(dayjs(), "day")) {
                              return PAST_DATE_ERROR_MSG;
                            }

                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <DesktopDatePicker
                            {...field}
                            className="date-picker defer-date-picker"
                            format={DATE_PICKER_FORMAT}
                            label={
                              <>
                                Due Date <span className="color-red">*</span>
                              </>
                            }
                            value={
                              watch("dueDate")
                                ? dayjs(watch("dueDate"))
                                : dayjs(new Date())
                            }
                            onChange={(date) => {
                              field.onChange(
                                date ? date.format(DATE_ONLY_FORMAT) : ""
                              );
                            }}
                            slots={{
                              openPickerIcon: CalendarIcon,
                            }}
                            minDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dueDate,
                                helperText: errors.dueDate?.message || "",
                              },
                            }}
                          />
                        )}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box className="modal-footer">
            <Button variant="contained" className="btn-save" type="submit">
              <img src={saveIcon} alt="save" />
              Save
            </Button>
            <Button
              variant="outlined"
              className="btn-cancel"
              onClick={handleCloseAllocateToPLA}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AllocateSLPModal;
